/*
 * Content
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';

import { Block } from '~/components/Block/Block';

export type ContentProps = {
  children?: React$Node,
  header?: React$Node,
  palette?: string,
  fullscreen?: boolean,
  device?: $PropertyType<DeviceQuery, 'device'>,
  orientation?: $PropertyType<DeviceQuery, 'orientation'>,
};

export type ContentHeaderProps = {
  children?: React$Node,
  palette?: string,
};

const ContentWrapper = styled(Block).attrs(() => ({
  elevation: 2,
}))`
  display: block;
  ${ifProp(
    { device: 'desktop' },
    css`
      margin: 15px 18px;
      padding: 35px;
    `,
    css`
      padding: 18px;
      height: auto !important;
      min-height: 100%;
      border-radius: 0;
    `,
  )};

  ${ifProp(
    'fullscreen',
    css`
      display: flex;
      flex-direction: column;
      padding-bottom: 0;
      height: calc(100% - 30px);
      overflow: auto;
    `,
  )};
`;

const ContentHeaderWrapper = styled.div`
  position: relative;
  ${ifProp(
    { device: 'desktop' },
    css`
      margin: -35px -35px 35px;
      padding: 35px;
      min-height: 30px;
    `,
    css`
      display: flex;
      margin: -18px -18px 18px;
      padding: 18px;
      min-height: 40px;
      align-items: center;
    `,
  )};
  background: #f7fbfe;

  p {
    font-size: 16px;
    line-height: normal;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: ${palette('grayscale', 0)};

    & + p {
      margin-top: 14px;
    }
  }
`;

/**
 * Content React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {React.Node<*>} [props.header] - header component
 * @param {boolean} [fullscreen] - show in 100%-height
 * @param {string} [props.palette = 'primary'] - current QR Code palette
 * @example <caption>Simple usage</caption>
 * <Content>My Wealth</Content>
 * @returns {React.Node<*>} React-component
 */
export const Content = ({
  header,
  palette,
  children,
  ...rest
}: ContentProps) => (
  <ContentWrapper testid="content" {...rest}>
    {header && (
      <ContentHeaderWrapper palette={palette}>{header}</ContentHeaderWrapper>
    )}
    {children}
  </ContentWrapper>
);

Content.Header = ContentHeaderWrapper;

Content.defaultProps = {
  palette: 'primary',
};
